"use client";

import { useSearchParams } from "next/navigation";
import DiscoverCard from "@/components/card/DiscoverCard";
import HomeCarouselSection from "../../HomeCarouselSection";
import { SiteFrame } from "@/components/layout/SiteFrame";
import FullImageCardSkeleton from "@/components/skeletons/card/PosterCardSkeleton";
import { Typography } from "@/components/Typography";
import { useGetDiscoverListing } from "@/hooks/useDiscover";
import { DiscoverItem } from "./discover.type";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useInView } from "@/hooks/useObserver";

export type DiscoverCarouselProps = {
  title: string;
  href?: string;
};

const DiscoverCarousel = ({
  title,
  href = "/discover?q=all",
}: DiscoverCarouselProps) => {
  const searchParams = useSearchParams();
  const showAll = searchParams.get("q") === "all";
  const [containerRef, inView] = useInView("300px");
  const isMdUp = useMediaQuery("(min-width: 768px)");
  const {
    data: discovers,
    isFetching,
    isError,
  } = useGetDiscoverListing(showAll || inView);

  if (showAll) {
    return (
      <SiteFrame outerClassName="flex h-full">
        <div className="mb-5 mt-4">
          <Typography variant="h5">All Discoveries</Typography>
          {!isFetching && !isError && (
            <Typography variant="h6" className="mt-1 text-muted-foreground">
              {discovers?.length ?? 0} results
            </Typography>
          )}
        </div>

        {isError ? (
          <div className="py-16 text-center text-muted-foreground">
            Something went wrong loading discoveries.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {isFetching
              ? Array.from({ length: 8 }).map((_, i) => (
                  <FullImageCardSkeleton key={i} />
                ))
              : discovers?.map((discover) => (
                  <DiscoverCard key={discover.id} discover={discover} />
                ))}
          </div>
        )}

        {!isFetching && !isError && discovers?.length === 0 && (
          <div className="py-16 text-center text-muted-foreground">
            No discoveries found.
          </div>
        )}
      </SiteFrame>
    );
  }

  return (
    <div ref={containerRef}>
      <HomeCarouselSection<DiscoverItem>
        title={title}
        href={href}
        items={discovers}
        isFetching={isFetching}
        isError={isError}
        itemsPerCol={isMdUp ? 4 : 3}
        getKey={(discover) => discover.id}
        renderItem={(discover) => <DiscoverCard discover={discover} />}
        renderSkeleton={() => <FullImageCardSkeleton />}
      />
    </div>
  );
};

export default DiscoverCarousel;
